import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Settings } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CookieBanner = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    marketing: false,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = async (marketingConsent, analyticsConsent) => {
    try {
      await axios.post(`${API}/cookie-consent`, {
        consent_given: true,
        marketing_consent: marketingConsent,
        analytics_consent: analyticsConsent,
      });
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  };

  const handleAcceptAll = async () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      marketing: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    }));
    await saveConsent(true, true);
    setShowBanner(false);
  };

  const handleReject = async () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      marketing: false,
      analytics: false,
      timestamp: new Date().toISOString(),
    }));
    await saveConsent(false, false);
    setShowBanner(false);
  };

  const handleSavePreferences = async () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString(),
    }));
    await saveConsent(preferences.marketing, preferences.analytics);
    setShowBanner(false);
    setShowCustomize(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 cookie-banner p-4 md:p-6 shadow-lg animate-fade-in"
      style={{ zIndex: 9999 }}
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto">
        {!showCustomize ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1 text-center md:text-start">
              <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('cookie_title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('cookie_message')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                data-testid="cookie-reject-btn"
              >
                {t('cookie_reject')}
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50 transition-colors flex items-center gap-2"
                data-testid="cookie-customize-btn"
              >
                <Settings size={14} />
                {t('cookie_customize')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="btn-primary text-sm py-2"
                data-testid="cookie-accept-all-btn"
              >
                {t('cookie_accept_all')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('cookie_customize')}
              </h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium">{t('cookie_necessary')}</span>
                  <p className="text-xs text-gray-500 mt-1">{t('cookie_necessary_desc')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="w-5 h-5 rounded accent-sage-600"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <div>
                  <span className="font-medium">{t('cookie_analytics')}</span>
                  <p className="text-xs text-gray-500 mt-1">{t('cookie_analytics_desc')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="w-5 h-5 rounded accent-sage-600"
                  data-testid="cookie-analytics-checkbox"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                <div>
                  <span className="font-medium">{t('cookie_marketing')}</span>
                  <p className="text-xs text-gray-500 mt-1">Personalized content and ads</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                  className="w-5 h-5 rounded accent-sage-600"
                  data-testid="cookie-marketing-checkbox"
                />
              </label>
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
              >
                {t('cookie_reject')}
              </button>
              <button
                onClick={handleSavePreferences}
                className="btn-primary text-sm py-2"
                data-testid="cookie-save-btn"
              >
                {t('cookie_save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
